import { useRef } from 'react';
import { useWheelNavigation } from '../hooks/useWheelNavigation';
import { useStore } from '../store/useStore';
import { useLiveQuery } from 'dexie-react-hooks';
import { AccountingEngine } from '../services/accountingEngine';

export const BalanceSheetView = ({ config, handleItemSelect, selectedIndex, setSelectedIndex }: any) => {
  const scrollRef = useRef(null);
  const activeCompany = useStore((state) => state.activeCompany);
  const fromDate = useStore((state) => state.fromDate);
  const toDate = useStore((state) => state.toDate);
  
  const bsData = useLiveQuery(async () => {
    if (!activeCompany) return null;
    return await AccountingEngine.generateBalanceSheet(activeCompany.id!, fromDate, toDate);
  }, [activeCompany, fromDate, toDate]);

  const leftItems = bsData?.liabilities || [];
  const rightItems = bsData?.assets || [];
  const flatItems = [...leftItems, ...rightItems];
  
  useWheelNavigation(scrollRef, flatItems.length, setSelectedIndex, false, () => flatItems && flatItems[selectedIndex] && handleItemSelect(flatItems[selectedIndex], selectedIndex));

  if (!bsData) {
    return (
      <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-black justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e8585]"></div>
        <div className="mt-4 font-bold text-gray-500">Calculating Balance Sheet...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[12px] text-black">
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm z-10 text-[13px]">
        <span className="w-1/3 text-left">Balance Sheet</span>
        <span className="w-1/3 text-center">{activeCompany?.name || config.companyList[0].name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Side: Liabilities */}
        <div className="w-1/2 flex flex-col border-r border-gray-300">
          <div className="flex justify-between border-b border-gray-300 font-bold px-2 py-1 leading-tight items-end">
            <span className="mb-[2px]">Liabilities</span>
            <span className="text-right">{activeCompany?.name}<br/><span className="font-normal">as at {toDate}</span></span>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto pt-1 pb-2">
             {leftItems.map((item: any, idx: number) => {
               const isSelected = selectedIndex === idx;
               return (
                 <div key={idx} onClick={() => handleItemSelect(item, idx)} className={`flex flex-col px-2 py-[2px] cursor-pointer ${isSelected ? 'bg-[#1e8585] text-white font-bold active-row' : 'text-black font-bold'}`}>
                    <div className="flex justify-between w-full">
                       <span>{item.name}</span><span>{item.amount.toFixed(2)}</span>
                    </div>
                 </div>
               )
             })}
          </div>
          <div className="flex justify-between border-t border-gray-400 font-bold px-2 py-1">
             <span>Total</span><span>{bsData?.totalLiabilities.toFixed(2) || '0.00'}</span>
          </div>
        </div>

        {/* Right Side: Assets */}
        <div className="w-1/2 flex flex-col">
          <div className="flex justify-between border-b border-gray-300 font-bold px-2 py-1 leading-tight items-end">
            <span className="mb-[2px]">Assets</span>
            <span className="text-right">{activeCompany?.name}<br/><span className="font-normal">as at {toDate}</span></span>
          </div>
          <div className="flex-1 overflow-y-auto pt-1 pb-2">
             {rightItems.map((item: any, idx: number) => {
               const globalIdx = leftItems.length + idx;
               const isSelected = selectedIndex === globalIdx;
               return (
                 <div key={globalIdx} onClick={() => handleItemSelect(item, globalIdx)} className={`flex flex-col px-2 py-[2px] cursor-pointer ${isSelected ? 'bg-[#1e8585] text-white font-bold active-row' : 'text-black font-bold'}`}>
                    <div className="flex justify-between w-full">
                       <span>{item.name}</span><span>{item.amount.toFixed(2)}</span>
                    </div>
                 </div>
               )
             })}
          </div>
          <div className="flex justify-between border-t border-gray-400 font-bold px-2 py-1">
             <span>Total</span><span>{bsData?.totalAssets.toFixed(2) || '0.00'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
